import { forwardRef, Ref, useMemo } from "react";
import cx from "classnames";

import { SelectMenuSync } from "../../../../../../beta/components/SelectMenu/SelectMenuSync";
import { SelectMenuOption } from "../../../../../../beta/components/SelectMenu/types";

import { SrOnly } from "../../../../../../components/SrOnly";

import { DataTableEditableCellEditIcon } from "./DataTableEditableCellEditIcon";
import { TableBodyCell } from "../../../base/cells/TableBodyCell";

import styles from "./DataTableEditableCells.module.scss";
import { EditableCellImperativeHandle } from "./types";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { mergeRefs } from "../../../../../../hooks/useMergeRefs/useMergeRefs";
import { BaseEditableCellProps } from "./types";
import { getColumnId, getCellValue } from "../util/cellTypeHelpers";
import { DataTableBodyImmutableCell } from "../cells/DataTableBodyImmutableCell";
import { CellFocusContext } from "../cells/CellFocusContext";
import { useEditableMenuCell } from "./useEditableMenuCell";

export type DataTableEditableSelectCellProps<T> = BaseEditableCellProps<T>;

/**
 * Renders an editable select cell in a DataTable. This component provides a SelectMenu popover
 * that appears when the cell is activated, allowing users to select a single value from a list of
 * options. Supports keyboard navigation and commit/cancel operations.
 *
 * @param props - The props for the DataTableEditableSelectCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellProps - Props for styling and event handling of the cell container
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to display when not editing
 * @param props.isHovered - Whether the cell is currently hovered
 * @param ref - Ref to the imperative handle for programmatic control
 * @returns The rendered editable select cell component
 */
function DataTableEditableSelectCellInner<T>(
  props: DataTableEditableSelectCellProps<T>,
  ref: React.Ref<EditableCellImperativeHandle>,
) {
  const { cell, cellProps, cellPosition, children } = props;
  const { onKeyDown: onCellKeyDown, className, ...restCellProps } = cellProps;

  const columnId = getColumnId(cell);
  const cellValue = getCellValue(cell, columnId);

  const meta = cell.column.columnDef.meta;
  const editConfig = meta?.editConfig;
  const selectEditConfig =
    editConfig?.mode === "select" ? editConfig : undefined;

  const options: SelectMenuOption[] = useMemo(
    () => selectEditConfig?.options ?? [],
    [selectEditConfig],
  );

  const onChange = selectEditConfig?.onChange;

  const {
    mode: _mode,
    options: _options,
    onChange: _onChange,
    ...selectMenuPassthroughProps
  } = selectEditConfig ?? {};

  const isValidValueType = useMemo(() => {
    if (Array.isArray(cellValue)) return false;
    return true;
  }, [cellValue]);

  const rowId = cell.row.id;

  const {
    tabIndex,
    cellRef,
    triggerClickRef,
    handleCellKeyDown,
    handleMenuKeyDown,
    handleImplicitClose,
    handleCellMouseDown,
    handleCellBlur,
    handleCellFocus,
    cellFocusContextValue,
  } = useEditableMenuCell({ cellPosition, onCellKeyDown, ref });

  // Find selected option by matching cell value to option id
  const selectedOption = useMemo(() => {
    if (cellValue === undefined || cellValue === null) return null;
    return options.find((o) => String(o.id) === String(cellValue)) ?? null;
  }, [options, cellValue]);

  const hasEditableConfig = !!selectEditConfig;

  if (!isValidValueType || !hasEditableConfig) {
    const { cellProps: _cellProps, ...immutableCellProps } = props;
    return <DataTableBodyImmutableCell {...immutableCellProps} />;
  }

  return (
    <SelectMenuSync
      trigger={({
        ref: triggerRef,
        onClick,
        onKeyDown: menuKeyDown,
        "aria-controls": ariaControls,
        "aria-expanded": ariaExpanded,
        "aria-haspopup": ariaHaspopup,
        "data-state": dataState,
      }) => {
        triggerClickRef.current = onClick;
        const isOpen = ariaExpanded;
        return (
          <TableBodyCell
            ref={mergeRefs([
              triggerRef as Ref<HTMLElement>,
              cellRef as Ref<HTMLElement>,
            ])}
            tabIndex={tabIndex}
            type="data-table"
            {...restCellProps}
            className={cx(styles["data-table-body-cell-editable"], className)}
            onMouseDown={handleCellMouseDown(onClick)}
            onBlur={handleCellBlur}
            onFocus={handleCellFocus}
            data-cell-pos={formatCellPositionString(cellPosition)}
            aria-controls={ariaControls}
            aria-expanded={ariaExpanded}
            aria-haspopup={ariaHaspopup}
            data-state={dataState}
            onKeyDown={(e) => handleCellKeyDown(e, menuKeyDown, isOpen)}
          >
            <CellFocusContext.Provider value={cellFocusContextValue}>
              {children}
              {!isOpen && <DataTableEditableCellEditIcon />}
            </CellFocusContext.Provider>
            <SrOnly>
              {isOpen
                ? "Press Enter or Space to select a cell value. Press Escape or F2 to cancel."
                : "Press Enter or F2 to edit cell value."}
            </SrOnly>
          </TableBodyCell>
        );
      }}
      label={String(
        cell.column.columnDef.meta?.columnDef?.headerLabel ?? "Select",
      )}
      options={options}
      value={selectedOption}
      onSelectedOptionChange={(option) => {
        onChange?.(option, rowId);
      }}
      onMenuKeyDown={handleMenuKeyDown}
      onImplicitClose={handleImplicitClose}
      {...selectMenuPassthroughProps}
    />
  );
}

export const DataTableEditableSelectCell = Object.assign(
  forwardRef(DataTableEditableSelectCellInner) as <T>(
    props: DataTableEditableSelectCellProps<T> & {
      ref?: React.Ref<EditableCellImperativeHandle>;
    },
  ) => React.ReactElement,
  { displayName: "DataTableEditableSelectCell" },
);
